/**
 * Project Bobcat — the "what to do once the new solution loads" markers
 * Create sets (`tableStore.pendingAiNativeSwap` and its siblings), kept in
 * the tab's session storage as well as in memory.
 *
 * Create marks the solution, navigates, and the markers are consumed when
 * its rows first load. A page reload in between — a deploy landing
 * mid-session, a refresh, Vite re-optimizing deps in dev — used to lose
 * them: the solution kept Create's Gemini Supervisor, its agents ungrouped,
 * its tasks stubs (seen live, Oct 27). Session storage survives the reload
 * and stays in the tab that created the solution. Pure apart from the
 * storage it is handed; every read and write tolerates blocked storage.
 */

export const CREATE_MARKERS_KEY = 'pro-studio:create-markers';

/** Older than this, the create they belong to is long over. */
export const CREATE_MARKERS_TTL_MS = 30 * 60 * 1000;

/** The store fields that are create markers. */
export const CREATE_MARKER_FIELDS = [
  'pendingAutoPublish',
  'pendingAutoAgentGrouping',
  'pendingSkipKnowledgeCorpus',
  'pendingAiNativeSwap',
  'pendingEmptySolution',
  'pendingUseCaseAgentGrouping',
  'pendingCanvasLanding',
  'pendingAgentDrafts',
] as const;

export type CreateMarkerField = (typeof CREATE_MARKER_FIELDS)[number];
export type CreateMarkers = Partial<Record<CreateMarkerField, unknown>>;

interface Saved {
  at: number;
  markers: CreateMarkers;
}

type Storage = Pick<globalThis.Storage, 'getItem' | 'setItem' | 'removeItem'>;

/** Only the markers that are set. */
const setOnly = (markers: CreateMarkers): CreateMarkers =>
  Object.fromEntries(
    CREATE_MARKER_FIELDS.filter((f) => markers[f] != null).map((f) => [
      f,
      markers[f],
    ])
  );

/** Saves the markers that are set, or clears the entry when none are. */
export const saveCreateMarkers = (
  storage: Storage | null | undefined,
  markers: CreateMarkers,
  now = Date.now()
) => {
  if (!storage) return;
  const set = setOnly(markers);
  try {
    if (!Object.keys(set).length) {
      storage.removeItem(CREATE_MARKERS_KEY);
      return;
    }
    storage.setItem(
      CREATE_MARKERS_KEY,
      JSON.stringify({ at: now, markers: set } satisfies Saved)
    );
  } catch {
    // Blocked or full storage: the in-memory markers still work.
  }
};

/** The saved markers, unless missing, unreadable or stale. */
export const loadCreateMarkers = (
  storage: Storage | null | undefined,
  now = Date.now()
): CreateMarkers => {
  if (!storage) return {};
  try {
    const raw = storage.getItem(CREATE_MARKERS_KEY);
    if (!raw) return {};
    const saved = JSON.parse(raw) as Partial<Saved>;
    if (
      typeof saved?.at !== 'number' ||
      now - saved.at > CREATE_MARKERS_TTL_MS ||
      !saved.markers ||
      typeof saved.markers !== 'object'
    ) {
      storage.removeItem(CREATE_MARKERS_KEY);
      return {};
    }
    return setOnly(saved.markers);
  } catch {
    return {};
  }
};

/** The tab's session storage, or null where there is none. */
export const sessionStorageOrNull = (): Storage | null => {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
};
